import { CategoriaEnum, defineAbilitiesFor } from '@acme/auth'
import { AppAbility } from '@acme/auth/types'
import Cookies from 'js-cookie'
import jwt_decode from 'jwt-decode'
import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router-dom'
import {
    FuncionarioUsuario,
    Login,
    Token,
    User,
    auth as auth_api,
} from 'service/api'
import tokenRefresher from 'service/api/token-refresher'
import { toast } from 'sonner'
import { rememberMeStore } from 'templates/Auth/store'
import { TokenDecoded } from 'types'
import { useStore } from 'store'
import { queryClient } from '../providers/ReactQuery'

interface AuthContextProps {
    auth: boolean
    loading: boolean
    decoded: TokenDecoded
    handleLogout: () => void
    handleLogin: SubmitHandler<(Login | Token) & { navigateTo?: string }>
    user: User
    funcionarioSelecionado?: FuncionarioUsuario
    selecionarFuncionario: (funcionario: FuncionarioUsuario) => void
    permissions: AppAbility
}

type Props = {
    children: React.ReactNode
}

export const AuthContext = createContext({} as AuthContextProps)

function safeDecodeToken(token?: string) {
    try {
        return jwt_decode<TokenDecoded>(token || '')
    } catch (_) {
        console.error('Token inválido')
        return {} as TokenDecoded
    }
}

export const AuthContextProvider: React.FC<Props> = ({ children }) => {
    const navigate = useNavigate()
    const { setEmail } = rememberMeStore()
    const [isLoading, setIsLoading] = useState(false)
    const { mutateAsync } = auth_api.login.mutations.useCreate()

    const tokenCookie = Cookies.get('JWT')

    const [auth, setAuth] = useState(!!tokenCookie)
    const [loggedUser, setLoggedUser] = useState<User>({} as User)
    const funcionarioSelecionado = JSON.parse(
        String(sessionStorage.getItem('funcionario')),
    )
    const selecionarFuncionario = (
        funcionario: FuncionarioUsuario | undefined | null,
    ) => {
        if (funcionario)
            sessionStorage.setItem('funcionario', JSON.stringify(funcionario))
        else sessionStorage.removeItem('funcionario')
    }

    const [tokenDecode, setTokenDecode] = useState(() =>
        safeDecodeToken(tokenCookie),
    )

    useEffect(() => {
        if (tokenCookie) {
            setTokenDecode(safeDecodeToken(tokenCookie))
            setAuth(true)
        }
    }, [tokenCookie])

    const handleLogin: SubmitHandler<
        (Login | Token) & { navigateTo?: string }
    > = useCallback(
        async data => {
            setIsLoading(true)
            try {
                Cookies.remove('JWT')
                Cookies.remove('refreshToken')
                selecionarFuncionario(null)

                let accessToken: Token['token'],
                    refreshToken: Token['refreshToken'],
                    user: Token['user'],
                    rememberMe: Login['rememberMe']

                if ('email' in data) {
                    const response = await mutateAsync(data, {
                        onError: e => {
                            throw e
                        },
                    })
                    accessToken = response.token
                    refreshToken = response.refreshToken
                    user = response.user
                    rememberMe = data.rememberMe
                } else {
                    accessToken = data.token
                    refreshToken = data.refreshToken
                    user = data.user
                    rememberMe = false
                }
                setLoggedUser(user)

                useStore.persist.clearStorage()
                useStore.setState({
                    companyId: -1,
                    company: null,
                    employeeId: -1,
                    employee: null,
                    costCenterId: -1,
                    costCenter: null,
                    locationId: -1,
                    location: null,
                })

                Cookies.set('JWT', accessToken, { expires: 1, sameSite: 'lax' })
                if (refreshToken)
                    Cookies.set('refreshToken', refreshToken, {
                        expires: 7,
                        sameSite: 'lax',
                    })

                const decode = jwt_decode<TokenDecoded>(accessToken)

                rememberMeStore.setState({
                    emailAvaliacao: null,
                    openAvaliacao: null,
                })

                setAuth(true)

                rememberMe && setEmail(decode.email)
                setTokenDecode(decode)

                const len = user?.Funcionario?.length ?? 0
                if (data.navigateTo) {
                    if (decode?.categoria === CategoriaEnum.Funcionario)
                        selecionarFuncionario(
                            user?.Funcionario?.at(0) ?? ({} as any),
                        )
                    navigate(data.navigateTo)
                    return
                } else if (
                    decode?.categoria === CategoriaEnum.Funcionario &&
                    len > 1
                )
                    navigate('/selecionar-funcionario')
                else if (decode?.categoria === CategoriaEnum.Funcionario)
                    selecionarFuncionario(
                        user?.Funcionario?.at(0) ?? ({} as any),
                    )
                else navigate('/dashboard')
            } catch (err: any) {
                toast.error(err.message ?? 'Erro ao efetuar login')
            } finally {
                setIsLoading(false)
            }
        },
        [setEmail, navigate],
    )

    async function handleLogout() {
        setAuth(false)
        queryClient.removeQueries()
        sessionStorage.clear()
        useStore.persist.clearStorage()
        tokenRefresher.logoff(false)
        return <Navigate to="/" />
    }

    const value: AuthContextProps = {
        auth,
        handleLogin,
        handleLogout,
        loading: isLoading,
        decoded: tokenDecode,
        user: loggedUser,
        funcionarioSelecionado,
        selecionarFuncionario,
        permissions: useMemo(() => {
            try {
                if (!auth)
                    return defineAbilitiesFor({
                        categoriaId: 0,
                        isAtivo: false,
                        modulos: [],
                    })

                return defineAbilitiesFor({
                    categoriaId: tokenDecode?.categoria,
                    modulos: tokenDecode?.modulos,
                    isAtivo: tokenDecode?.isAtivo,
                    userAppAbilities: tokenDecode?.userAbilities,
                })
            } catch (_) {
                return defineAbilitiesFor({ categoriaId: 0, isAtivo: true })
            }
        }, [auth, tokenDecode]),
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
